import { Grid } from '@mui/material';
import PageSection from 'components/PageSection';
import ErrorBoundary from 'components/ErrorBoundary';
import RecordView from 'components/RecordView';
import Spacing from 'components/Spacing';
import Button from 'components/Button';
import React from 'react';
import { Vehicle } from 'further-types/lp/vehicle';
import FieldRow from 'components/FormElements/FieldRow';
import { Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import { compact, sumBy } from 'lodash';
import ButtonGroup from 'components/ButtonGroup';
import ConsiderationOverview from './ConsiderationOverview.tsx';
import CashConsiderationsTable from './CashConsiderationsTable';
import { Transaction } from 'components/TransactionAllocator';
import { CalendarDay } from 'further-ui/utils';
import useCurrencyRate from 'hooks/lp/data/currencies/useCurrencyRate';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import PageLoader from 'components/PageLoader';
import {
  ConsiderationType,
  ConsiderationTypeHoldingClassTypes,
} from 'further-types/lp/realisation';
import api from 'lib/trpcClient.ts';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation.ts';
import buildRealisationPayload from './helpers/buildRealisationPayload.ts';
import ReviewAccountTransactions from './ReviewAccountTransactions.tsx';
import getRealisationType from './transactions/helpers/getRealisationType.ts';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal/index.tsx';
import { useDisclosure } from 'further-ui/hooks';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';

type SummaryProps = {
  onSuccess: () => void;
  onPrevious: () => void;
  selectedVehicle: Vehicle;
};

const Summary: React.FC<SummaryProps> = ({
  onSuccess,
  onPrevious,
  selectedVehicle,
}) => {
  const { getValues, setValue } =
    useFormContext<rules.CreateRealisationFormValues>();
  const { hasEqualisationInDraftState } = useEqualisations(selectedVehicle._id);
  const confirmDialog = useDisclosure();

  const mutationOptions = useHandleTrpcMutation()({
    onSuccess: () => {
      onSuccess();
    },
    successMessage: 'Realisation created successfully',
    refetchType: 'none', // Invalidate but don't refetch
  });

  const holdingClassRealisation = getValues('holdingClassRealisation');

  const holdingClassInvestmentsWithUnitSold = holdingClassRealisation.flatMap(
    (holdingClass) => holdingClass.unitsAllocationPerInvestment ?? [],
  );

  const [saleOfInvestmentData] =
    api.lpRealisation.calculateSaleOfInvestment.useSuspenseQuery({
      vehicleId: selectedVehicle._id,
      holdingClassInvestments: holdingClassInvestmentsWithUnitSold,
    });

  const currentRealisationPerHoldingClassInvestment = compact(
    holdingClassRealisation.flatMap((holdingClass) =>
      holdingClass.unitsAllocationPerInvestment?.map((investment) => ({
        investmentId: investment.investmentId,
        holdingClassId: holdingClass.holdingClassId,
        unitsSold: investment.allocatedUnits ?? 0,
      })),
    ),
  );

  const [unrealisedGainsUnwindData] =
    api.lpRealisation.getUnrealisedGainsUnwind.useSuspenseQuery({
      assetId: getValues('assetId'),
      vehicleId: selectedVehicle._id,
      realisationDate: getValues('realisationDate'),
      currentRealisationPerHoldingClassInvestment,
    });

  const [unrealisedAssetFXGainsAndLossesUnwinds] =
    api.unrealisedAssetFxGainOrLoss.getUnwind.useSuspenseQuery({
      vehicleId: selectedVehicle._id,
      holdingClassRealisations: currentRealisationPerHoldingClassInvestment,
      date: getValues('realisationDate'),
    });

  const unrealisedGainsUnwindPerHoldingClassInvestment =
    unrealisedGainsUnwindData ?? [];

  const [holdingClassInvestmentsConsiderationValues] =
    api.lpRealisation.getHoldingClassInvestmentsConsiderationValues.useSuspenseQuery(
      {
        holdingClassRealisations: holdingClassRealisation,
        withholdingTaxList: getValues('investorWithholdingTaxList') ?? [],
        vehicleId: selectedVehicle._id,
      },
    );

  const ledgerDate =
    getValues('realisationDate') ?? new CalendarDay(new Date());

  const assetName = getValues('assetName');

  const considerations = getValues('consideration');

  const cashConsiderationIndex = considerations.findIndex(
    (consideration) =>
      consideration.considerationType ===
      ConsiderationType.CashAndWithholdingTax,
  );

  const investorAllocations =
    considerations[cashConsiderationIndex]?.investorAllocations;

  const considerationCurrency =
    considerations[cashConsiderationIndex]?.currency ??
    selectedVehicle.baseCurrency;

  const holdingClassCurrencies = holdingClassRealisation.flatMap(
    (holdingClass) =>
      holdingClass.unitsAllocationPerInvestment?.map(
        (investment) => investment.currency,
      ),
  );

  const holdingClassCurrency = holdingClassCurrencies[0];

  const isHoldingClassCurrencyDifferent = !holdingClassCurrencies.every(
    (currency) => currency === holdingClassCurrency,
  );

  if (isHoldingClassCurrencyDifferent && !holdingClassCurrency) {
    throw new Error('Holding class currencies are different.');
  }

  const realisationType = getRealisationType({
    holdingClassInvestment: {
      currency: holdingClassCurrency ?? considerationCurrency,
    },
    vehicleCurrencyCode: selectedVehicle.baseCurrency,
    realisationCurrencyCode: considerationCurrency,
  });

  const { mutateAsync: createRealisation, isPending } =
    api.lpRealisation.create.useMutation(mutationOptions);

  const handleSubmit = async () => {
    const cashConsideration = getValues('consideration').find(
      (consideration) =>
        consideration.considerationType ===
        ConsiderationType.CashAndWithholdingTax,
    );

    if (!cashConsideration || !holdingClassInvestmentsConsiderationValues) {
      throw new Error('Cash consideration not found.');
    }

    const payload = buildRealisationPayload({
      selectedVehicle,
      assetId: getValues('assetId'),
      realisationDate: getValues('realisationDate'),
      currencyRate: getValues('currencyRate'),
      holdingClassRealisation,
      consideration: getValues('consideration'),
      realisationType,
      holdingClassInvestmentUnwindedGains:
        unrealisedGainsUnwindPerHoldingClassInvestment.map((holdingClass) => ({
          holdingClassId: holdingClass.holdingClassId,
          investmentId: holdingClass.investmentId,
          unwindedGainsLcy: holdingClass.unrealisedGainsUnwindAtDateLcy,
          unwindedGainsTcy: holdingClass.unrealisedGainsUnwindAtDateTcy,
        })),
      cashConsideration,
      finalTransactions: getValues('transactions') ?? [],
      saleOfInvestment: saleOfInvestmentData,
      //@ts-expect-error Needs to be fixed - Realisations
      holdingClassInvestmentConsiderationValues:
        holdingClassInvestmentsConsiderationValues,
    });

    await createRealisation(payload);
  };

  const currencyForTcyDisplay =
    realisationType ===
    ConsiderationTypeHoldingClassTypes.LcyConsiderationForNonLcyHoldingClass
      ? holdingClassCurrency ?? considerationCurrency
      : considerationCurrency;

  const isThreeCyFxRateRealisation =
    realisationType ===
    ConsiderationTypeHoldingClassTypes.ThreeCYNonLcyConsiderationNonLcyHoldingClass;

  const {
    fxRate: threeCyFxRate,
    changeRate: threeCyChangeRate,
    isFetchingRate: threeCyIsFetchingRate,
  } = useCurrencyRate({
    vehicleId: selectedVehicle._id,
    currency: holdingClassCurrency ?? considerationCurrency,
    date: ledgerDate,
    defaultCurrencyRate: getValues('threeCyCurrencyRate') ?? null,
    onRateChange: (rate) => {
      setValue('threeCyCurrencyRate', rate ?? undefined);
    },
  });

  const { fxRate, changeRate, isFetchingRate } = useCurrencyRate({
    vehicleId: selectedVehicle._id,
    currency: currencyForTcyDisplay,
    date: ledgerDate,
    defaultCurrencyRate: getValues('currencyRate') ?? null,
    onRateChange: (rate) => {
      setValue('currencyRate', rate ?? undefined);
    },
  });

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const onChangeTransaction = (transactions: Array<Transaction>) => {
    setValue('transactions', transactions);
  };

  if (transactionTypesLoading) {
    return <PageLoader />;
  }

  return (
    <ErrorBoundary>
      <PageSection title="Transaction summary">
        <Spacing size="md">
          <Grid container>
            <Grid size={{ xs: 12, md: 8 }}>
              <RecordView>
                <FieldRow title="Vehicle">
                  <Typography variant="body2">
                    {selectedVehicle.vehicleName}
                  </Typography>
                </FieldRow>
                <FieldRow title="Realised asset">
                  <Typography variant="body2">{assetName}</Typography>
                </FieldRow>
                <FieldRow title="Realisation date">
                  <Typography variant="body2">
                    {getValues('realisationDate')?.toLabel()}
                  </Typography>
                </FieldRow>
                <FieldRow title="Classes realised">
                  <Typography variant="body2">
                    {holdingClassRealisation
                      .map((holdingClass) => holdingClass.name)
                      .join(', ')}
                  </Typography>
                </FieldRow>
                <FieldRow title="Total units realised">
                  <Typography variant="body2">
                    {sumBy(holdingClassRealisation, 'unitsSold')}
                  </Typography>
                </FieldRow>
                <CashConsiderationsTable
                  currency={considerationCurrency}
                  holdingClassRealisations={holdingClassRealisation}
                />
              </RecordView>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <ConsiderationOverview
              currency={considerationCurrency}
              holdingClassRealisations={holdingClassRealisation}
              investorAllocations={investorAllocations}
            />
          </Grid>
          <PageSection title="Review account transactions">
            <ReviewAccountTransactions
              selectedVehicle={selectedVehicle}
              currency={considerationCurrency}
              currencyForTcyDisplay={currencyForTcyDisplay}
              holdingClassCurrency={
                holdingClassCurrency ?? considerationCurrency
              }
              ledgerDate={ledgerDate}
              fxRate={fxRate}
              changeRate={changeRate}
              isFetchingRate={isFetchingRate}
              isThreeCyFxRateRealisation={isThreeCyFxRateRealisation}
              threeCyFxRate={threeCyFxRate}
              threeCyChangeRate={threeCyChangeRate}
              threeCyIsFetchingRate={threeCyIsFetchingRate}
              transactionTypeOptions={transactionTypeOptions}
              onChangeTransaction={onChangeTransaction}
              unrealisedGainsUnwindPerHoldingClassInvestment={
                unrealisedGainsUnwindPerHoldingClassInvestment
              }
              unrealisedAssetFXGainsAndLossesUnwinds={
                unrealisedAssetFXGainsAndLossesUnwinds
              }
              saleOfInvestmentData={saleOfInvestmentData}
              holdingClassRealisation={holdingClassRealisation}
              //@ts-expect-error Needs to be fixed - Realisations
              holdingClassInvestmentConsiderationValues={
                holdingClassInvestmentsConsiderationValues ?? []
              }
            />
          </PageSection>
        </Spacing>
      </PageSection>
      <ButtonGroup>
        <Button variant="outlined" onClick={onPrevious}>
          Previous step
        </Button>
        <Button
          variant="contained"
          color="primary"
          loading={isPending}
          onClick={handleSubmit}
        >
          Save draft
        </Button>
        <Button
          variant="contained"
          color="primary"
          loading={isPending}
          onClick={
            hasEqualisationInDraftState ? confirmDialog.onOpen : handleSubmit
          }
        >
          Save and approve
        </Button>
        {hasEqualisationInDraftState && (
          <PendingDraftEqualisationsModal
            isOpen={confirmDialog.isOpen}
            onClose={confirmDialog.onClose}
            onConfirm={handleSubmit}
            loading={isPending}
          />
        )}
      </ButtonGroup>
    </ErrorBoundary>
  );
};

export default Summary;
